export const config = {
    appName: "Swiftia | AI clip editing Platform",
    swiftia: {
        apiKey: process.env.SWIFTIA_API_KEY,
        webhookSecret: process.env.SWIFTIA_WEBHOOK_SECRET ?? "verySecretCode",
    },
    n8n: {
        webhookSecret: process.env.N8N_WEBHOOK_SECRET ?? "DdAz1okBhO53XIZXm54Hevuio9c7ZRZYk2D2n0xJCQ6pMRHPQoNrsEJeu0wA1mGq",
    },
    // Internationalization
    i18n: {
        // Whether internationalization should be enabled (if disabled, you still need to define the locale you want to use below and set it as the default locale)
        enabled: false,
        // Define all locales here that should be available in the app
        // You need to define a label that is shown in the language selector and a currency that should be used for pricing with this locale
        locales: {
            en: {
                currency: "USD",
                label: "English",
            },
            de: {
                currency: "USD",
                label: "Deutsch",
            },
        },
        // The default locale is used if no locale is provided
        defaultLocale: "en",
        // The default currency is used for pricing if no currency is provided
        defaultCurrency: "USD",
        // The name of the cookie that is used to determine the locale
        localeCookieName: "NEXT_LOCALE",
    },
    // Organizations
    organizations: {
        // Whether organizations are enabled in general
        enable: true,
        // Whether billing for organizations should be enabled (below you can enable it for users instead)
        enableBilling: true,
        // Whether the organization should be hidden from the user (use this for multi-tenant applications)
        hideOrganization: false,
        // Should users be able to create new organizations? Otherwise only admin users can create them
        enableUsersToCreateOrganizations: true,
        // Whether users should be required to be in an organization. This will redirect users to the organization page after sign in
        requireOrganization: true,
        // Define forbidden organization slugs. Make sure to add all paths that you define as a route after /app/... to avoid routing issues
        forbiddenOrganizationSlugs: [
            "new-organization",
            "admin",
            "settings",
            "ai-demo",
            "organization-invitation",
        ],
    },
    // Users
    users: {
        // Whether billing should be enabled for users (above you can enable it for organizations instead)
        enableBilling: false,
        // Whether you want the user to go through an onboarding form after signup (can be defined in the OnboardingForm.tsx)
        enableOnboarding: true,
    },
    // Authentication
    auth: {
        // Whether users should be able to create accounts (otherwise users can only be by admins)
        enableSignup: true,
        // Whether users should be able to sign in with a magic link
        enableMagicLink: false,
        // Whether users should be able to sign in with a social provider
        enableSocialLogin: true,
        // Whether users should be able to sign in with a passkey
        enablePasskeys: false,
        // Whether users should be able to sign in with a password
        enablePasswordLogin: true,
        // Whether users should be activate two factor authentication
        enableTwoFactor: true,
        // where users should be redirected after the sign in
        redirectAfterSignIn: "/app",
        // where users should be redirected after logout
        redirectAfterLogout: "/",
        // how long a session should be valid
        sessionCookieMaxAge: 60 * 60 * 24 * 30,
    },
    // Mails
    mails: {
        // the from address for mails
        from: "support@swiftia.io",
    },
    // Frontend
    ui: {
        // the themes that should be available in the app
        enabledThemes: ["light", "dark"],
        // the default theme
        defaultTheme: "light",
        // the saas part of the application
        saas: {
            // whether the saas part should be enabled (otherwise all routes will be redirect to the marketing page)
            enabled: true,
            // whether the sidebar layout should be used
            useSidebarLayout: true,
        },
        // the marketing part of the application
        marketing: {
            // whether the marketing features should be enabled (otherwise all routes will be redirect to the saas part)
            enabled: true,
        },
    },
    // Phone verification
    phoneVerification: {
        // Globally enable/disable phone verification enforcement
        enabled: false,
        // Default requirement if country override not found
        defaultRequired: true,
        // Try WhatsApp first (Twilio) and fallback to SMS if it fails
        whatsappEnabled: true,
        // Country-specific overrides (ISO2 -> required?)
        countryOverrides: {
        // Example: FR: false, US: true
        },
        // Minimum remaining lifetime (seconds) under which user can request a resend
        // e.g. if a code expires in 10 minutes, block resends while > 8 minutes remain
        minResendBufferSeconds: 8 * 60,
        // Maximum verification attempts for a single code
        maxVerificationAttempts: 3,
        // Planned: max resends within a sliding window (not yet enforced)
        maxResendPerWindow: 5,
        resendWindowSeconds: 60 * 60,
    },
    // Storage
    storage: {
        // define the name of the buckets for the different types of files
        bucketNames: {
            avatars: process.env.NEXT_PUBLIC_AVATARS_BUCKET_NAME ?? "swiftia-avatars",
            files: process.env.NEXT_PUBLIC_FILES_BUCKET_NAME ?? "swiftia-files",
        },
        // Custom domain for CloudFront (if configured)
        customDomain: process.env.NEXT_PUBLIC_S3_CUSTOM_DOMAIN ?? "pprd-cdn.swiftia.io",
    },
    contactForm: {
        // whether the contact form should be enabled
        enabled: true,
        // the email to which the contact form messages should be sent
        to: "support@swiftia.io",
        // the subject of the email
        subject: "Contact form message",
    },
    // Payments
    payments: {
        // define the products that should be available in the checkout
        plans: {
            // The free plan is treated differently. It will automatically be assigned if the user has no other plan.
            free: {
                isFree: true,
                storage: 300,
                resolution: {
                    width: 720,
                    height: 1280,
                },
                maxFileUpload: 100,
                pricePerProcessedMinute: 0.03, // $1.8/hour
                pricePerBRollShort: 0.05, // per short (B-roll generation)
                trialAmount: 2,
                trialPeriodDays: 7, // days
                maxOrganizations: 1, // Free users can only have 1 organization
                maxMembersPerOrganization: 1, // Free users cannot invite members
                canResize: false, // Free users can only use default portrait format
                maxVideoDuration: 120, // seconds
                bRollEnabled: false,
                rateLimit: {
                    maxRequests: 1, // 1 request per window
                    windowMs: 5000, // 5 seconds
                },
                socialPostingEnabled: false,
            },
            starter: {
                recommended: false,
                pricePerProcessedMinute: 0.03, // $1.8/hour
                pricePerBRollShort: 0.1, // $0.10 per short (B-roll generation)
                resolution: {
                    width: 720,
                    height: 1280,
                },
                prices: [
                    {
                        type: "recurring",
                        productId: process.env
                            .NEXT_PUBLIC_PRICE_ID_STARTER_MONTHLY,
                        interval: "month",
                        amount: 9,
                        currency: "USD",
                        seatBased: false,
                        trialPeriodDays: 0,
                    },
                    {
                        type: "recurring",
                        productId: process.env
                            .NEXT_PUBLIC_PRICE_ID_STARTER_YEARLY,
                        interval: "year",
                        amount: 102,
                        currency: "USD",
                        seatBased: false,
                        trialPeriodDays: 0,
                    },
                ],
                storage: 5000,
                maxFileUpload: 200,
                maxOrganizations: 3, // Pro users can have up to 3 organizations
                maxMembersPerOrganization: 5, // Pro users can invite up to 5 members per organization
                canResize: false, // Starter users can only use default portrait format
                maxVideoDuration: 300, // seconds (5 minutes)
                bRollEnabled: false,
                rateLimit: {
                    maxRequests: 1, // 1 requests per window
                    windowMs: 2000, // 2 seconds
                },
                socialPostingEnabled: false,
            },
            pro: {
                recommended: true,
                pricePerProcessedMinute: 0.026, // $1.8/hour
                pricePerBRollShort: 0.06, // $0.06 per short (B-roll generation)
                resolution: {
                    width: 1080,
                    height: 1920,
                },
                prices: [
                    {
                        type: "recurring",
                        productId: process.env
                            .NEXT_PUBLIC_PRICE_ID_PRO_MONTHLY,
                        interval: "month",
                        amount: 48,
                        currency: "USD",
                        seatBased: false,
                        trialPeriodDays: 0,
                    },
                    {
                        type: "recurring",
                        productId: process.env
                            .NEXT_PUBLIC_PRICE_ID_PRO_YEARLY,
                        interval: "year",
                        amount: 540,
                        currency: "USD",
                        seatBased: false,
                        trialPeriodDays: 0,
                    },
                ],
                storage: 10000,
                maxFileUpload: 200,
                maxOrganizations: 3, // Pro users can have up to 3 organizations
                maxMembersPerOrganization: 5, // Pro users can invite up to 5 members per organization
                canResize: true, // Pro users can resize to any format within plan limits
                maxVideoDuration: 600, // seconds (10 minutes)
                bRollEnabled: false,
                rateLimit: {
                    maxRequests: 1, // 1 requests per window
                    windowMs: 500, // 0.5 seconds
                },
                socialPostingEnabled: false,
            },
            swift: {
                pricePerProcessedMinute: 0.023, // $1.4/hour
                pricePerBRollShort: 0.02, // $0.02 per short (B-roll generation)
                resolution: {
                    width: 1080,
                    height: 1920,
                },
                prices: [
                    {
                        type: "recurring",
                        productId: process.env
                            .NEXT_PUBLIC_PRICE_ID_PREMUIM_MONTHLY,
                        interval: "month",
                        amount: 280,
                        currency: "USD",
                        seatBased: false,
                        trialPeriodDays: 0,
                    },
                    {
                        type: "recurring",
                        productId: process.env
                            .NEXT_PUBLIC_PRICE_ID_PREMUIM_YEARLY,
                        interval: "year",
                        amount: 3120,
                        currency: "USD",
                        seatBased: false,
                        trialPeriodDays: 0,
                    },
                ],
                storage: 20000,
                maxFileUpload: 500,
                maxOrganizations: 10, // Premium users can have up to 10 organizations
                maxMembersPerOrganization: 20, // Premium users can invite up to 20 members per organization
                canResize: true, // Swift users can resize to any format within plan limits
                maxVideoDuration: 3600, // seconds (1 hour)
                bRollEnabled: false,
                rateLimit: {
                    maxRequests: 5, // 5 requests per window
                    windowMs: 500, // 1 seconds
                },
                socialPostingEnabled: true,
            },
            /** Contact Swiftia to subscribe. Shown on pricing; Stripe price id is used when a subscription is created in Stripe (e.g. manually) so the app can resolve the plan. */
            managedTier1: {
                isEnterprise: true,
                pricePerProcessedMinute: 0.023,
                pricePerBRollShort: 0.02,
                resolution: { width: 1080, height: 1920 },
                prices: [
                    {
                        type: "recurring",
                        productId: process.env
                            .NEXT_PUBLIC_PRICE_ID_MANAGED_TIER_1_MONTHLY,
                        interval: "month",
                        amount: 499,
                        currency: "USD",
                        seatBased: false,
                        trialPeriodDays: 0,
                    },
                    {
                        type: "recurring",
                        productId: process.env
                            .NEXT_PUBLIC_PRICE_ID_MANAGED_TIER_1_YEARLY,
                        interval: "year",
                        amount: 5500,
                        currency: "USD",
                        seatBased: false,
                        trialPeriodDays: 0,
                    },
                ],
                storage: 20000,
                maxFileUpload: 500,
                maxOrganizations: 10,
                maxMembersPerOrganization: 20,
                canResize: true,
                maxVideoDuration: 3600,
                bRollEnabled: false,
                rateLimit: { maxRequests: 10, windowMs: 500 },
                socialPostingEnabled: true,
            },
            managedTier2: {
                isEnterprise: true,
                pricePerProcessedMinute: 0.023,
                pricePerBRollShort: 0.02,
                resolution: { width: 1080, height: 1920 },
                prices: [
                    {
                        type: "recurring",
                        productId: process.env
                            .NEXT_PUBLIC_PRICE_ID_MANAGED_TIER_2_MONTHLY,
                        interval: "month",
                        amount: 899,
                        currency: "USD",
                        seatBased: false,
                        trialPeriodDays: 0,
                    },
                    {
                        type: "recurring",
                        productId: process.env
                            .NEXT_PUBLIC_PRICE_ID_MANAGED_TIER_2_YEARLY,
                        interval: "year",
                        amount: 9889,
                        currency: "USD",
                        seatBased: false,
                        trialPeriodDays: 0,
                    },
                ],
                storage: 20000,
                maxFileUpload: 500,
                maxOrganizations: 10,
                maxMembersPerOrganization: 20,
                canResize: true,
                maxVideoDuration: 3600,
                bRollEnabled: false,
                rateLimit: { maxRequests: 10, windowMs: 500 },
                socialPostingEnabled: true,
            },
            managedTier3: {
                isEnterprise: true,
                pricePerProcessedMinute: 0.023,
                pricePerBRollShort: 0.02,
                resolution: { width: 1080, height: 1920 },
                prices: [
                    {
                        type: "recurring",
                        productId: process.env
                            .NEXT_PUBLIC_PRICE_ID_MANAGED_TIER_3_MONTHLY,
                        interval: "month",
                        amount: 1999,
                        currency: "USD",
                        seatBased: false,
                        trialPeriodDays: 0,
                    },
                    {
                        type: "recurring",
                        productId: process.env
                            .NEXT_PUBLIC_PRICE_ID_MANAGED_TIER_3_YEARLY,
                        interval: "year",
                        amount: 21989,
                        currency: "USD",
                        seatBased: false,
                        trialPeriodDays: 0,
                    },
                ],
                storage: 20000,
                maxFileUpload: 500,
                maxOrganizations: 10,
                maxMembersPerOrganization: 20,
                canResize: true,
                maxVideoDuration: 3600,
                bRollEnabled: false,
                rateLimit: { maxRequests: 10, windowMs: 500 },
                socialPostingEnabled: true,
            },
        },
    },
};
